import {DOCUMENT} from '@angular/common';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {getLockIconConfig} from '@application/helper/get-lock-icon-config';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {IdNameUsed} from '@domain/id-name-used';
import {HttpStockLocationService} from '@infrastructure/http/stock-location/http-stock-location.service';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  CanLeavePage,
  ContentSwitcherNavigationInput,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  LockIconConfig,
  NavigationHelperPrototypeService,
  SaveType,
  TabsMenuItem,
  TranslateService
} from '@vdw/angular-component-library';
import {Observable, takeUntil} from 'rxjs';
import {StockLocationForm} from './add-stock-location.form';

@Component({
  selector: 'app-add-stock-location',
  templateUrl: './add-stock-location.component.html'
})
export class AddStockLocationComponent extends BaseComponent implements OnInit, CanLeavePage {
  @Input() public pageType: PageTypeEnum;
  @Input() public itemId: number;
  @Input() public canShowHeader: boolean = false;

  public lockIconConfig: LockIconConfig;
  public addStockLocationForm: StockLocationForm;
  public readonly SAVE_TYPE = SaveType;
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_STOCK_LOCATION;
  public selectedMenuItem: TabsMenuItem = {value: 0, translationKey: 'GENERAL.GENERAL'};
  public readonly STOCK_LOCATION_TRANSLATION_KEY = 'UTILITIES.TOOL.STOCK_LOCATION';
  private used = false;

  public constructor(
    private readonly formBuilder: FormBuilder,
    private readonly stockLocationService: HttpStockLocationService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    public readonly navigationHelper: NavigationHelperPrototypeService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setFormFields();
    this.getStockLocationDetails();
  }

  public isEditingStockLocation(): boolean {
    return this.pageType === PageTypeEnum.EDIT;
  }

  public canLeavePage(): boolean {
    return !this.addStockLocationForm.dirty || this.saving;
  }

  public isLocked(): boolean {
    return this.isEditingStockLocation() && this.used;
  }

  public get createdObject(): string {
    return !AssertionUtils.isNullOrUndefined(this.addStockLocationForm.controls.name.value) ? this.addStockLocationForm.controls.name.value : '';
  }

  public getLoadEntry(): ContentSwitcherNavigationInput {
    return {
      componentInfo: {
        component: AddStockLocationComponent,
        componentInputs: {pageType: PageTypeEnum.EDIT, itemId: this.addStockLocationForm?.controls?.name?.errors?.entityId, canShowHeader: true}
      }
    };
  }

  public save(saveType: SaveType): void {
    const isValid = new FormValidationHelper().checkForm(this.addStockLocationForm, this.document);
    if (isValid) {
      this.saving = true;
      const stockLocationToSave = this.getCurrentStockLocation();

      const request: Observable<void | number> = this.isEditingStockLocation() ? this.stockLocationService.update(stockLocationToSave) : this.stockLocationService.save(stockLocationToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe({
        next: (id: number) => {
          if (saveType === SaveType.SAVE_AND_CLOSE) {
            stockLocationToSave.id = id;

            this.navigationHelper.navigateBack(this.isEditingStockLocation() ? [] : stockLocationToSave);
          } else if (saveType === SaveType.SAVE_AND_CREATE_NEW) {
            this.resetFormFields();
          }
        },
        complete: () => {
          this.finalizeSaving();
        },
        error: (errorMessage: BackendError) =>
          this.showErrorDialogForBackendError(this.isEditingStockLocation() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  private getStockLocationDetails(): void {
    const id = this.itemId;

    if (!AssertionUtils.isNullOrUndefined(id)) {
      this.stockLocationService
        .getById(Number(id))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((stockLocation: IdNameUsed) => {
          this.itemId = stockLocation.id;
          this.used = stockLocation.used;

          this.setFormValues(stockLocation);
          this.setLockIconConfig(stockLocation);
        });
    }
  }

  private setFormValues(stockLocation: IdNameUsed): void {
    this.addStockLocationForm.reset({
      name: this.pageType === PageTypeEnum.DUPLICATE ? null : stockLocation.name
    });

    if (this.isLocked()) {
      this.addStockLocationForm.disable();
    }
  }

  private setLockIconConfig(stockLocation: IdNameUsed): void {
    if (this.isLocked()) {
      this.lockIconConfig = getLockIconConfig(stockLocation.name, this.stockLocationService.getConflicts(stockLocation.id));
    }
  }

  private getCurrentStockLocation(): IdNameUsed {
    return new IdNameUsed(this.isEditingStockLocation() ? this.itemId : null, this.addStockLocationForm.value.name, this.used);
  }

  private setFormFields(): void {
    this.addStockLocationForm = this.formBuilder.group({
      name: this.formBuilder.control('', Validators.required, AsyncUniqueValidator.createValidator(this.stockLocationService, null))
    });
  }

  private resetFormFields(): void {
    this.addStockLocationForm.reset();
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {object: this.translate.instant(this.STOCK_LOCATION_TRANSLATION_KEY)}),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }
}
